/**
 * TableCard Component
 *
 * A reusable component that displays data in a structured table format within a card layout.
 * Commonly used for showing key-value pair information like details, settings, or properties.
 *
 * Features:
 * - Optional title header
 * - Two-column layout (label-value pairs)
 * - Optional edit button in top-right corner
 * - Responsive design with consistent styling
 * - Customizable through className prop
 */

import React from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";
import { Edit } from "lucide-react";
import { Icons } from "./icons";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

/**
 * Represents a single row item in the table
 * @property {string} label - The label/key displayed in the left column
 * @property {React.ReactNode} value - The value displayed in the right column (supports any valid React node)
 */
interface TableItem {
  label: string;
  value: React.ReactNode;
}

/**
 * Props for the TableCard component
 * @property {string} [title] - Optional header text for the card
 * @property {TableItem[]} data - Array of label-value pairs to display in the table
 * @property {string} [className] - Optional CSS classes for additional styling
 * @property {() => void} [onEdit] - Optional callback function for edit button click
 */
interface TableCardProps {
  title?: string;
  data: TableItem[];
  className?: string;
  onEdit?: () => void;
  tooltipText?: string;
  titleAction?: React.ReactNode;
}

export function TableCard({
  title,
  data,
  className = "",
  onEdit,
  tooltipText,
  titleAction,
}: TableCardProps) {
  return (
    <div className={`rounded-md border p-2 relative ${className}`}>
      {onEdit && (
        <Button
          variant="outline"
          size="sm"
          className="absolute top-4 right-4"
          onClick={onEdit}
        >
          <Edit /> Edit
        </Button>
      )}
      {title && (
        <div className="font-medium p-4 flex items-center gap-2">
          {title}
          {titleAction}
          {tooltipText && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <Icons.help className="h-4 w-4 text-muted-foreground" />
                </TooltipTrigger>
                <TooltipContent>
                  <p>{tooltipText}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      )}
      <div className="font-medium px-2">
        <Table className="text-gray-500 text-xs leading-6">
          <TableBody>
            {data.map((item, index) => (
              <TableRow key={index}>
                <TableCell className="font-medium p-2 w-1/3">
                  {item.label}
                </TableCell>
                <TableCell>{item.value}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
